import {
  Tabs as KobalteTabs,
  type TabsContentProps,
  type TabsListProps,
  type TabsTriggerProps,
} from "@kobalte/core/tabs";
import { Transition } from "solid-transition-group";
import { createMemo, type JSX, type ParentProps, splitProps } from "solid-js";
import { cn } from "@comandero/shared";

/**
 * Primitivo Tabs compartido y animado construido sobre Kobalte `Tabs`.
 *
 * El comportamiento se mantiene 100% de Kobalte (foco, navegación por teclado,
 * roles ARIA). Lo único que se añade es una transición GPU-only
 * (transform/opacity) del *panel activo* al cambiar de pestaña, keyada por el
 * valor activo, de modo que el panel entrante desliza + funde. La dirección del
 * slide sigue el índice relativo de la pestaña (adelante → entra desde la
 * derecha, atrás → desde la izquierda); si no se puede inferir el orden, cae a
 * un fade+rise neutro. `prefers-reduced-motion` se respeta vía el guard global
 * de `global.css` (las duraciones colapsan a ~0, sin movimiento).
 *
 * Uso:
 *   <Tabs value={tab()} onChange={setTab}>
 *     <TabsList>
 *       <TabsTrigger value="a">A</TabsTrigger>
 *       <TabsTrigger value="b">B</TabsTrigger>
 *     </TabsList>
 *     <TabsPanels value={tab()} order={["a", "b"]}>
 *       <TabsPanel value="a">…</TabsPanel>
 *       <TabsPanel value="b">…</TabsPanel>
 *     </TabsPanels>
 *   </Tabs>
 */

const Tabs = KobalteTabs;

function TabsList(props: TabsListProps) {
  const [local, rest] = splitProps(props as TabsListProps & { class?: string }, ["class"]);
  return (
    <KobalteTabs.List
      class={cn(
        "relative flex items-center gap-1 border-b border-border",
        "data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch data-[orientation=vertical]:border-b-0 data-[orientation=vertical]:border-r",
        local.class,
      )}
      {...rest}
    />
  );
}

function TabsTrigger(props: ParentProps<TabsTriggerProps>) {
  const [local, rest] = splitProps(props as ParentProps<TabsTriggerProps> & { class?: string }, [
    "class",
  ]);
  return (
    <KobalteTabs.Trigger
      class={cn(
        "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium",
        "cursor-pointer text-muted-foreground transition-[color,background-color]",
        "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "data-[selected]:text-foreground disabled:pointer-events-none disabled:opacity-50",
        local.class,
      )}
      {...rest}
    />
  );
}

function TabsIndicator(props: { class?: string }) {
  return (
    <KobalteTabs.Indicator
      class={cn(
        "absolute bg-accent transition-[transform,width,height] duration-200 ease-[var(--ease-elegant,ease)]",
        "bottom-0 h-0.5 data-[orientation=vertical]:bottom-auto data-[orientation=vertical]:right-0 data-[orientation=vertical]:h-auto data-[orientation=vertical]:w-0.5",
        props.class,
      )}
    />
  );
}

/**
 * Contenedor de paneles animados. Renderiza solo el panel activo dentro de un
 * `<Transition>` keyado, de modo que cambiar de pestaña funde/desliza. Pasa
 * `order` (los valores de pestaña en orden visual) para slides direccionales;
 * omítelo para fade+rise.
 *
 * Renderiza un único panel a la vez en vez de montar cada Kobalte
 * `Tabs.Content`, porque animar montaje/desmontaje es lo que produce la
 * transición. El etiquetado ARIA del panel se preserva vía `TabsPanel`
 * (Kobalte `Tabs.Content`).
 */
interface TabsPanelsProps extends ParentProps {
  /** Valor de la pestaña activa (controlado). */
  value: string;
  /** Valores de pestaña en orden visual; habilita el slide direccional. */
  order?: readonly string[];
  class?: string;
}

function TabsPanels(props: TabsPanelsProps) {
  // Instantánea del valor previamente activo, capturada en la primera corrida
  // del memo para que toda lectura de `props.*` quede dentro del scope rastreado
  // (solid/reactivity).
  let prev: string | undefined;

  const direction = createMemo<"forward" | "backward" | "none">(() => {
    // Todas las lecturas de `props.*` ocurren aquí, dentro del scope del memo.
    const from = prev;
    prev = props.value;
    if (from === undefined || !props.order || props.order.length === 0) {
      return "none";
    }
    const fromIdx = props.order.indexOf(from);
    const toIdx = props.order.indexOf(props.value);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) {
      return "none";
    }
    return toIdx > fromIdx ? "forward" : "backward";
  });

  const name = () => {
    const d = direction();
    if (d === "forward") {
      return "tab-panel-forward";
    }
    if (d === "backward") {
      return "tab-panel-backward";
    }
    return "tab-panel-fade";
  };

  return (
    <div class={cn("relative", props.class)}>
      <Transition name={name()} mode="outin">
        {/* Keyado por el valor activo: cambiar de pestaña desmonta el panel
            viejo y monta el nuevo, disparando la transición enter/exit. */}
        <KeyedPanel key={props.value}>{props.children}</KeyedPanel>
      </Transition>
    </div>
  );
}

/** Se remonta cuando cambia `key` para que el `<Transition>` padre anime. */
function KeyedPanel(props: { key: string; children: JSX.Element }) {
  return (
    <div data-tab-panel={props.key} class="w-full">
      {props.children}
    </div>
  );
}

/**
 * Un único panel (Kobalte `Tabs.Content`). `forceMount` se usa para que el panel
 * dentro del wrapper keyado se renderice siempre mientras es el activo; el
 * wrapper `TabsPanels` es dueño del montaje/desmontaje. El ARIA (role=tabpanel,
 * aria-labelledby) viene de Kobalte sin cambios.
 */
function TabsPanel(props: ParentProps<TabsContentProps & { class?: string }>) {
  const [local, rest] = splitProps(props as ParentProps<TabsContentProps & { class?: string }>, [
    "class",
  ]);
  return (
    <KobalteTabs.Content
      forceMount
      class={cn("outline-none focus-visible:outline-none", local.class)}
      {...rest}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsIndicator, TabsPanels, TabsPanel };
